/**
 * Azure AD Authentication Configuration
 * 
 * Configures Microsoft Authentication Library (MSAL) for Azure AD B2B authentication.
 * Handles token management, caching, and logging based on environment settings.
 * 
 * Features:
 * - LocalStorage-based token caching
 * - Automatic token refresh
 * - Environment-aware logging (disabled in production and for PII)
 * - Redirect URI configuration for OAuth flow
 */

import { PublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';

import { environment } from './environment';

/**
 * MSAL Configuration Object
 * 
 * @property {Object} auth - Authentication settings
 * @property {string} auth.clientId - Azure AD application ID
 * @property {string} auth.authority - Azure AD tenant authority URL
 * @property {string} auth.redirectUri - OAuth redirect URI after login
 * @property {boolean} auth.navigateToLoginRequestUrl - Skip navigation to login request URL
 * @property {Object} cache - Token caching configuration
 * @property {string} cache.cacheLocation - LocalStorage for persistent token storage
 * @property {boolean} cache.storeAuthStateInCookie - Disable cookie-based auth state
 * @property {Object} system - System-level configuration
 * @property {Object} system.loggerOptions - Logger configuration
 */
export const msalConfig = {
  auth: {
    clientId: environment.azure.clientId,
    authority: environment.azure.authority,
    redirectUri: environment.azure.redirectUri,
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: false
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: string, containsPii: boolean) => {
        if (containsPii || environment.production) {
          return;
        }
        switch (level) {
          case 0: // Error
            console.error(message);
            return;
          case 1: // Warning
            console.warn(message);
            return;
          case 2: // Info
            // console.info(message);
            return;
          case 3: // Verbose
            // console.debug(message);
            return;
        }
      }
    }
  }
};

/**
 * MSAL Public Client Application Instance
 * 
 * Singleton instance for handling authentication flows, token acquisition,
 * and account management throughout the application lifecycle.
 * 
 * @type {PublicClientApplication}
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Login Request Configuration
 * 
 * Specifies API scopes required for user authentication and API access.
 * Scopes are obtained from environment configuration.
 * 
 * @property {string[]} scopes - Array of API access scopes from environment
 * @example
 * // Used in authentication flows to request specific permissions
 * msalInstance.loginPopup(loginRequest);
 */
export const loginRequest = {
  scopes: environment.azure.scopes
};
